import { create } from 'zustand';
import {
  PricingFilterScope,
  PricingFilterSelections,
  PricingLastInvoiceFilterValue,
  PricingRangeBound,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';

type ScopedSelections = Partial<
  Record<PricingFilterScope, PricingFilterSelections>
>;

interface PricingFiltersStore {
  clearAll: (scope: PricingFilterScope) => void;
  clearSection: (scope: PricingFilterScope, sectionId: string) => void;
  hydrate: (
    scope: PricingFilterScope,
    selections: PricingFilterSelections,
  ) => void;
  pruneSections: (
    scope: PricingFilterScope,
    allowedSectionIds: string[],
  ) => void;
  selections: ScopedSelections;
  setRangeBound: (
    scope: PricingFilterScope,
    sectionId: string,
    bound: 'max' | 'min',
    value: number | null,
  ) => void;
  togglePill: (
    scope: PricingFilterScope,
    sectionId: string,
    value: string,
    singleSelect: boolean,
  ) => void;
}

const EMPTY_RANGE_BOUND: PricingRangeBound = { max: null, min: null };
const EMPTY_SELECTIONS: PricingFilterSelections = { pills: {}, ranges: {} };

export const usePricingFiltersStore = create<PricingFiltersStore>(
  (set, get) => ({
    clearAll: (scope) =>
      set((state) => ({
        selections: { ...state.selections, [scope]: { pills: {}, ranges: {} } },
      })),
    clearSection: (scope, sectionId) =>
      set((state) => {
        const current = state.selections[scope] ?? EMPTY_SELECTIONS;
        const pills = { ...current.pills };
        const ranges = { ...current.ranges };
        delete pills[sectionId];
        delete ranges[sectionId];
        return {
          selections: { ...state.selections, [scope]: { pills, ranges } },
        };
      }),
    hydrate: (scope, selections) =>
      set((state) => ({
        selections: { ...state.selections, [scope]: selections },
      })),
    pruneSections: (scope, allowedSectionIds) => {
      const allowed = new Set(allowedSectionIds);
      const { pills, ranges } = get().selections[scope] ?? EMPTY_SELECTIONS;

      if (
        Object.keys(pills).every((id) => allowed.has(id)) &&
        Object.keys(ranges).every((id) => allowed.has(id))
      ) {
        return;
      }

      const nextPills = Object.fromEntries(
        Object.entries(pills).filter(([id]) => allowed.has(id)),
      );
      const nextRanges = Object.fromEntries(
        Object.entries(ranges).filter(([id]) => allowed.has(id)),
      );

      set((state) => ({
        selections: {
          ...state.selections,
          [scope]: { pills: nextPills, ranges: nextRanges },
        },
      }));
    },
    selections: {},
    setRangeBound: (scope, sectionId, bound, value) =>
      set((state) => {
        const current = state.selections[scope] ?? EMPTY_SELECTIONS;
        return {
          selections: {
            ...state.selections,
            [scope]: {
              ...current,
              ranges: {
                ...current.ranges,
                [sectionId]: {
                  ...(current.ranges[sectionId] ?? EMPTY_RANGE_BOUND),
                  [bound]: value,
                },
              },
            },
          },
        };
      }),
    togglePill: (scope, sectionId, value, singleSelect) =>
      set((state) => {
        const current = state.selections[scope] ?? EMPTY_SELECTIONS;
        const currentValues = current.pills[sectionId] ?? [];
        const isSelected = currentValues.includes(value);

        const next = singleSelect
          ? isSelected
            ? []
            : [value]
          : isSelected
            ? currentValues.filter((v) => v !== value)
            : [...currentValues, value];

        const leavingCustom =
          currentValues.includes(PricingLastInvoiceFilterValue.Custom) &&
          !next.includes(PricingLastInvoiceFilterValue.Custom);

        return {
          selections: {
            ...state.selections,
            [scope]: {
              ...current,
              pills: { ...current.pills, [sectionId]: next },
              ranges: leavingCustom
                ? { ...current.ranges, [sectionId]: EMPTY_RANGE_BOUND }
                : current.ranges,
            },
          },
        };
      }),
  }),
);
